import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Skins visuales (paletas + tipografías) portados de la web mundoatletismo.
 *  Se aplican como clase `skin-*` en <html>; los tokens --ld-* se redefinen
 *  por skin en global.css. El render del vídeo/imagen también usa este id. */
export type SkinId = 'editorial' | 'retro' | 'lane';

export const SKIN_IDS: SkinId[] = ['editorial', 'retro', 'lane'];

export const SKIN_LABEL: Record<SkinId, string> = {
  editorial: 'Editorial',
  retro: 'Retro',
  lane: 'Lane',
};

const SKIN_KEY = 'podios-stories.skin';
const DEFAULT_SKIN: SkinId = 'retro';

interface SkinState {
  skin: SkinId;
  setSkin: (s: SkinId) => void;
}

const SkinContext = createContext<SkinState | null>(null);

function isSkin(v: string | null): v is SkinId {
  return !!v && (SKIN_IDS as readonly string[]).includes(v);
}

function readStoredSkin(): SkinId {
  if (typeof window === 'undefined') return DEFAULT_SKIN;
  const urlSkin = new URL(window.location.href).searchParams.get('skin');
  if (isSkin(urlSkin)) return urlSkin;
  const v = window.localStorage.getItem(SKIN_KEY);
  return isSkin(v) ? v : DEFAULT_SKIN;
}

function applyToRoot(skin: SkinId) {
  const html = document.documentElement;
  SKIN_IDS.forEach((s) => html.classList.remove(`skin-${s}`));
  html.classList.add(`skin-${skin}`);
}

export function SkinProvider({ children }: { children: ReactNode }) {
  const [skin, setSkinState] = useState<SkinId>(() => readStoredSkin());

  useEffect(() => {
    applyToRoot(skin);
    window.localStorage.setItem(SKIN_KEY, skin);
  }, [skin]);

  const setSkin = useCallback((s: SkinId) => setSkinState(s), []);
  const value = useMemo(() => ({ skin, setSkin }), [skin, setSkin]);

  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
}

export function useSkin(): SkinState {
  const ctx = useContext(SkinContext);
  if (!ctx) throw new Error('useSkin must be used within <SkinProvider>');
  return ctx;
}
